import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { toast } from 'react-toastify';

import {
  getStripeRecurring,
  getStripeRecurringCheckoutUrl,
  getStripeSubscriptionsCheckoutUrl,
  SubscriptionPack,
} from '../../api/subscription';
import { AnimatedSpinner } from '../../components/AnimatedSpinner';
import {
  useCustomerPortalLink,
  useRecurlyCustomerPortalUrl,
  useSubscriptionDetails,
} from '../../hooks/apiHooks/subscriptionHooks';
import { OidcUser } from '../../types/oidcUser';
import type { Plan } from '../../types/subscription';
import { getOrganizationId, isUserAdmin } from '../../utils/getOrganization';
import MondiaBillingInfo from '../MyAccount/components/BillingTab/MondiaBillingInfo';
import { FAQ } from './components/FAQ';
import { PricingCard } from './components/PricingCard';

import { Link, useSearchParams } from 'react-router-dom';
import { useSubscriptionProductPacks } from '../../hooks/apiHooks/subscriptionHooks';
import { BillingToggle } from './components/BillingToggle';
import { PackCard } from './components/PackCard';
import { SubscriptionCards } from './components/SubscriptionCards';

const getHoursPerMonth = (units: number, interval?: string): number => {
  const delimiter = interval === 'year' ? 12 : 1;
  const hoursTotal = units / 60;
  return hoursTotal / delimiter;
};

const freePlan: Plan = {
  priceId: '',
  productId: '',
  name: 'Free',
  description: '100 minutes of chat or 10 songs',
  price: 0,
  features:
    '{"items":[{"text":"Thousands of AI companions"},{"text":"Music generation"},{"text":"Unlimited listening - audiobooks, meditation, music"}],"isPopular":false}',
  units: 100,
  interval: '',
  per: '',
  hours: '',
  credits: '',
};

function SubscriptionPage() {
  const { t } = useTranslation();
  const timerId = React.useRef<number | null>(null);
  const { user, isAuthenticated } = useAuth();
  const init = React.useRef(true);
  const [pricingPlans, setPricingPlans] = useState<Plan[]>([]);
  const [isAnnual, setIsAnnual] = useState(false);

  const [loading, setLoading] = useState(false);
  const [freeSubsName, setFreeSubsName] = useState<string>('');

  const [isTokenExpired, setIsTokenExpired] = useState(false);

  const orgId = getOrganizationId(user as OidcUser);
  const isCurrentUserAdmin = isUserAdmin(user as OidcUser, orgId);

  const [searchParams, setSearchParams] = useSearchParams();

  const [purchasedSubscriptionName] = useState<string>(
    searchParams.get('purchasedSubscription') || '',
  );

  const {
    data: subscriptionData,
    isError: isSubscriptionError,
    error: subscriptionError,
    isLoading: isSubscriptionLoading,
  } = useSubscriptionDetails(
    { organization_id: orgId },
    isAuthenticated,
    purchasedSubscriptionName,
  );

  const billingProvider = subscriptionData?.billingDetails?.provider;

  const { data: recurlyCustomerPortalUrlData } = useRecurlyCustomerPortalUrl(
    billingProvider === 'recurly',
  );

  const recurlyUrl = recurlyCustomerPortalUrlData?.data.url;

  useEffect(() => {
    if (searchParams.get('purchasedSubscription')) {
      toast.success(
        t('pricing.purchaseSuccess', {
          plan: searchParams.get('purchasedSubscription'),
        }),
      );
      searchParams.delete('purchasedPackName');
      setSearchParams(searchParams);
    }

    if (searchParams.get('purchasedSubscription')) {
      searchParams.delete('purchasedSubscription');
      setSearchParams(searchParams);
    }
  }, []);

  const {
    data: customLinkData,
    isError: isCustomerLinkError,
    error: customerLinkError,
    isLoading: isCustomerLinkLoading,
    refetch,
  } = useCustomerPortalLink({ token: user?.access_token || '' });

  const { data: subscriptionPacks } = useSubscriptionProductPacks();

  const handleAuthError = () => {
    setIsTokenExpired(true);
    if (timerId.current) {
      clearInterval(timerId.current);
      timerId.current = null;
    }
  };

  useEffect(() => {
    const isAnnual = subscriptionData?.billingDetails.billingPeriod === 'year';
    setIsAnnual(isAnnual);

    if (
      subscriptionData &&
      user?.access_token &&
      !isTokenExpired &&
      subscriptionData?.billingDetails.paymentType !== 'Free'
    ) {
      void refetch();
      timerId.current = window.setInterval(() => {
        void refetch();
      }, 2 * 60000);
    }
    return () => {
      if (timerId.current) clearInterval(timerId.current);
    };
  }, [subscriptionData, user?.access_token, isTokenExpired]);

  useEffect(() => {
    if (isSubscriptionError) {
      if (subscriptionError.status === 401) {
        handleAuthError();
      }
    }
  }, [isSubscriptionError, subscriptionError]);

  useEffect(() => {
    if (isCustomerLinkError) {
      if (customerLinkError.status === 401) {
        handleAuthError();
      }
    }
  }, [isCustomerLinkError, customerLinkError]);

  // useEffect(() => {
  //   if (timerId.current) clearInterval(timerId.current);
  //   return () => {
  //     // Cleanup timer on unmount
  //   };
  // }, []);

  useEffect(() => {
    if (init.current) {
      setLoading(true);
      getStripeRecurring()
        .then((response) => {
          const out = response.data
            .sort((a: Plan, b: Plan) => a.price - b.price)
            .map((item: Plan) => {
              if (item.price === 0) {
                setFreeSubsName(item.name);
              }
              return {
                ...item,
                per: item.interval,
                hours: getHoursPerMonth(item.units, item.interval) + '/month',
              };
            });
          setPricingPlans(out);
          init.current = false;
        })
        .catch((error: unknown) => {
          if (error && typeof error === 'object' && 'error' in error) {
            toast.error(error.error as string);
          }
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [user?.access_token]);

  const groupedPlans = useMemo(() => {
    const monthly = [freePlan];
    const annual = [freePlan];

    pricingPlans.forEach((plan) => {
      if (plan.per === 'month') {
        monthly.push(plan);
        return;
      }
      if (plan.per === 'year') {
        annual.push({ ...plan, units: plan.units / 12 });
      }
    });

    return {
      monthly,
      annual,
    };
  }, [pricingPlans]);

  const packs = useMemo(() => {
    if (!subscriptionPacks || subscriptionPacks.data.length === 0) {
      return [[], []];
    }
    const hardwarePack: SubscriptionPack[] = [];
    const packs = subscriptionPacks.data
      .sort((a: SubscriptionPack, b: SubscriptionPack) => a.price - b.price)
      .filter((pack) => {
        if (pack.isHardware === true) {
          hardwarePack.push(pack);
          return false;
        }
        return true;
      })
      .map((pack) => {
        return {
          ...pack,
          hours: String(pack.units / 60),
        };
      });

    return [packs, hardwarePack];
  }, [subscriptionPacks]);

  const handleCheckout = (
    price_id: string,
    cancelUrl: string,
    name: string,
  ) => {
    const returnUrl = cancelUrl + `?purchasedSubscription=${name}`;
    getStripeRecurringCheckoutUrl(price_id, returnUrl, cancelUrl)
      .then((response) => {
        window.location.href = response.data.url;
      })
      .catch((error: Record<string, string>) => {
        toast.error(error.error);
      });
  };

  const buyPacks = (price_id: string, cancelUrl: string, name: string) => {
    const returnUrl = cancelUrl + `?purchasedPackName=${name}`;
    getStripeSubscriptionsCheckoutUrl(price_id, returnUrl, cancelUrl)
      .then((response) => {
        window.location.href = response.data.url;
      })
      .catch((error: Record<string, string>) => {
        toast.error(error.error);
      });
  };

  const renderSubscriptionCards = () => {
    if (!isAuthenticated) {
      return (
        <>
          <div className="flex justify-center gap-4 flex-wrap [@media(min-width:1300px)]:flex-nowrap">
            <SubscriptionCards
              plans={isAnnual ? groupedPlans.annual : groupedPlans.monthly}
              onCheckout={handleCheckout}
              subPriceId={subscriptionData?.billingDetails.priceId}
              subsPlanName={subscriptionData?.billingDetails.planName}
              isAuthenticated={isAuthenticated}
              link={isAuthenticated ? customLinkData?.data.url : undefined}
              freeSubsName={freeSubsName}
              isLoading={
                isSubscriptionLoading ||
                isCustomerLinkLoading ||
                (!!purchasedSubscriptionName &&
                  subscriptionData?.billingDetails.planName !==
                    purchasedSubscriptionName)
              }
              isCurrentUserAdmin={isCurrentUserAdmin}
            />
          </div>
          <div className="text-white text-sm font-normal leading-[150%] mt-4">
            {t('pricing.disclaimer')}
          </div>
        </>
      );
    }
    switch (billingProvider) {
      case 'apple':
        return (
          <div className="rounded-regular p-6 tc-mv-blur text-white mt-4 text-left">
            <div className="text-white text-lg">
              {t('account.appleBillingInfo')}{' '}
              <a
                href="https://apps.apple.com/account/subscriptions"
                target="_blank"
                rel="noopener noreferrer"
                className="underline">
                {t('account.goHere')}
              </a>
              .
            </div>
          </div>
        );
      case 'google':
        return (
          <div className="rounded-regular p-6 tc-mv-blur text-white mt-4 text-left">
            <div className="text-white text-lg">
              {t('account.googleBillingInfo')}{' '}
              <a
                href="https://play.google.com/store/account/subscriptions"
                target="_blank"
                rel="noopener noreferrer"
                className="underline">
                {t('account.goHere')}
              </a>
              .
            </div>
          </div>
        );
      case 'mondia':
        return (
          <div className="rounded-regular p-6 tc-mv-blur text-white mt-4 text-left">
            <MondiaBillingInfo
              className="text-white text-lg"
              expiresAt={subscriptionData?.billingDetails?.expiresAt}
              operatorName={subscriptionData?.billingDetails?.operatorName}
            />
          </div>
        );
      case 'none':
      case 'recurly':
      case 'stripe':
        return (
          <>
            <div className="flex justify-center gap-4 flex-wrap [@media(min-width:1300px)]:flex-nowrap">
              <SubscriptionCards
                plans={isAnnual ? groupedPlans.annual : groupedPlans.monthly}
                onCheckout={handleCheckout}
                subPriceId={subscriptionData?.billingDetails.priceId}
                subsPlanName={subscriptionData?.billingDetails.planName}
                isAuthenticated={isAuthenticated}
                link={isAuthenticated ? customLinkData?.data.url : undefined}
                freeSubsName={freeSubsName}
                isLoading={
                  isSubscriptionLoading ||
                  isCustomerLinkLoading ||
                  (!!purchasedSubscriptionName &&
                    subscriptionData?.billingDetails.planName !==
                      purchasedSubscriptionName)
                }
                isCurrentUserAdmin={isCurrentUserAdmin}
              />
            </div>
            <div className="text-white text-sm font-normal leading-[150%] mt-4">
              {t('pricing.disclaimer')}
            </div>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <div className="h-full text-left opacity-100">
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-b from-white/10 to-white/0 -z-[1]" />

        <div className="sm:px-6 lg:px-8 py-8 overflow-y-auto h-full relative pb-60">
          <div className="fixed inset-0 w-full h-full bg-gradient-to-b from-white/10 to-white/0 -z-[1]" />
          <div className="bg-transparent transition-colors max-w-[1244px] mx-auto relative">
            {/* Hero Section */}
            <div className="mx-auto pt-10 px-5 sm:px-6 lg:px-8">
              <div className="flex justify-center items-center text-center text-white text-[48px] md:text-[64px] font-normal mb-6 font-['DM_Serif_Text'] leading-[56px]">
                {t('pricing.heroTitle')}
              </div>
              <div className="text-center mt-7 mb-16 ">
                <p className="text-xl text-white max-w-7xl mx-auto">
                  {t('pricing.heroSubtitle')}
                </p>
              </div>
              {/* Pricing Section */}
              {loading ? (
                <div className="flex justify-center items-center h-64">
                  <AnimatedSpinner className="w-8 h-8 text-white" />
                </div>
              ) : (
                <div className="mt-11">
                  {recurlyUrl && (
                    <div className="tc-mv-blur p-6 text-white mb-4 ">
                      <div className="flex items-center flex-wrap">
                        <div>
                          <p className="text-lg font-bold">
                            {t('pricing.oldPlanWarning')}
                          </p>
                          <p className="mt-2">{t('pricing.oldPlanMessage')}</p>
                        </div>
                        <a
                          href={recurlyUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="my-2 ml-auto h-[44px] px-6 bg-white hover:bg-[#F2F2F2] transition-colors duration-200 text-black rounded-[16px] font-medium font-[NapsterSans] whitespace-nowrap flex items-center justify-center">
                          {t('pricing.manageOldSubscription')}
                        </a>
                      </div>
                    </div>
                  )}
                  <div className="mt-11 shrink-0 text-white text-4xl font-normal mb-6 font-['DM_Serif_Text'] flex md:items-center items-start justify-between gap-5 md:flex-row flex-col ">
                    <span>{t('pricing.subscriptions')}</span>{' '}
                    {(billingProvider === 'stripe' ||
                      billingProvider === 'recurly' ||
                      billingProvider === 'none' ||
                      !isAuthenticated) && (
                      <div className="shrink-0">
                        <BillingToggle
                          isAnnual={isAnnual}
                          onChange={setIsAnnual}
                        />
                      </div>
                    )}
                  </div>
                  {renderSubscriptionCards()}

                  {billingProvider !== 'mondia' && (
                    <div className="mt-11">
                      <div className="text-white text-4xl font-normal mb-6 font-['DM_Serif_Text']">
                        {t('pricing.packs')}
                      </div>
                      <div className="flex justify-center gap-4 flex-col ">
                        {packs[0].map((pack) => (
                          <PackCard
                            key={pack.name}
                            {...pack}
                            onCheckout={buyPacks}
                            isAuthenticated={isAuthenticated}
                            isCurrentUserAdmin={isCurrentUserAdmin}
                          />
                        ))}
                      </div>
                      {/* anchor for scroll to view */}
                      <div id="view" />
                      {packs[1] && !!packs[1].length && (
                        <div className="mt-11">
                          <div className="text-white text-4xl font-normal mb-6 font-['DM_Serif_Text']">
                            {t('pricing.napsterView')}
                          </div>
                          <div className="text-white text-[18px] mb-6">
                            {t('pricing.napsterViewDesc')}{' '}
                            <Link
                              className="underline"
                              to="https://napster.ai/view"
                              target="_blank">
                              {t('pricing.napsterViewLink')}
                            </Link>
                          </div>
                          <PricingCard
                            key={packs[1][0].name}
                            {...packs[1][0]}
                            onCheckout={buyPacks}
                            isAuthenticated={isAuthenticated}
                            isHardware
                            isCurrentUserAdmin={isCurrentUserAdmin}
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
              <FAQ />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default SubscriptionPage;
